/**
 * Config Helper - ÚNICO PONTO DE ACESSO ÀS CONFIGURAÇÕES
 *
 * Regras:
 * - Local: vem do `.env` (Vite build-time)
 * - Azure: vem do `.env` criado no CI/CD com valores das GitHub Variables
 * - NÃO existe default/fallback para valores ausentes
 */

export interface RuntimeConfig {
  VITE_PROPERTY_SERVICE_URL: string;
  VITE_AUTH_SERVICE_URL: string;
  VITE_MUNICIPALITIES_SERVICE_URL: string;
  VITE_HUB_SERVICE_URL: string;
  // Novos services (canônica 2026-05-05 + F2-02e)
  VITE_CONSOLIDATE_SERVICE_URL: string;
  VITE_PERSISTENCE_SERVICE_URL: string;
  VITE_CLIMATE_SERVICE_URL: string;
  VITE_LEGAL_SERVICE_URL: string;
  VITE_GEOANALYSIS_SERVICE_URL: string;
  VITE_AIANALYSIS_SERVICE_URL: string;
  VITE_TILE_BASE_URL: string;
  VITE_TILE_PROVIDER: string;
  VITE_TILE_SAS: string;
  VITE_DEBUG: string;
  VITE_DEBUG_VERBOSE: string;
  VITE_DEFAULT_LOCALE: string;
  VITE_POLLING_INTERVAL_SECONDS: string;
  VITE_CORS_ENABLED: string;
  VITE_CORS_ORIGINS: string;
  VITE_CORS_METHODS: string;
  VITE_CORS_ALLOWED_HEADERS: string;
}

type ConfigKey = keyof RuntimeConfig;

// Variáveis que são obrigatórias (sem elas o app não funciona)
const REQUIRED_CONFIGS: ConfigKey[] = ['VITE_PROPERTY_SERVICE_URL', 'VITE_AUTH_SERVICE_URL'];

/**
 * Retorna o valor de uma configuração de import.meta.env
 * Para variáveis obrigatórias, lança erro se não existir
 * Para variáveis opcionais, retorna string vazia
 */
export function getConfig(key: ConfigKey): string {
  const envValue = import.meta.env[key];
  if (envValue && typeof envValue === 'string') {
    return envValue;
  }

  // Apenas lança erro para variáveis obrigatórias
  if (REQUIRED_CONFIGS.includes(key)) {
    throw new Error(`Missing required config: ${key}`);
  }

  // Para variáveis opcionais, retorna string vazia e loga warning
  console.warn(`[config-helper] Missing optional config: ${key}`);
  return '';
}

/**
 * Helpers específicos para cada configuração
 * USE APENAS ESTES MÉTODOS nos serviços!
 */
export const configHelper = {
  // URLs dos Serviços
  getPropertyServiceUrl: () => getConfig('VITE_PROPERTY_SERVICE_URL'),
  getAuthServiceUrl: () => getConfig('VITE_AUTH_SERVICE_URL'),
  getMunicipalitiesServiceUrl: () => getConfig('VITE_MUNICIPALITIES_SERVICE_URL'),
  getHubServiceUrl: () => getConfig('VITE_HUB_SERVICE_URL'),
  // Novos services (canônica 2026-05-05 + F2-02e)
  getConsolidateServiceUrl: () => getConfig('VITE_CONSOLIDATE_SERVICE_URL'),
  getPersistenceServiceUrl: () => getConfig('VITE_PERSISTENCE_SERVICE_URL'),
  getClimateServiceUrl: () => getConfig('VITE_CLIMATE_SERVICE_URL'),
  getLegalServiceUrl: () => getConfig('VITE_LEGAL_SERVICE_URL'),
  getGeoanalysisServiceUrl: () => getConfig('VITE_GEOANALYSIS_SERVICE_URL'),
  // G-25 (Wave 5): preparado para Fase 2 (ai-analysis orquestra enriquecimento)
  getAianalysisServiceUrl: () => getConfig('VITE_AIANALYSIS_SERVICE_URL'),

  // Tiles/Mapas
  getTileBaseUrl: () => getConfig('VITE_TILE_BASE_URL'),
  getTileProvider: () => getConfig('VITE_TILE_PROVIDER'),
  getTileSas: () => getConfig('VITE_TILE_SAS'),

  // CORS
  getCorsEnabled: () => getConfig('VITE_CORS_ENABLED') === 'true',
  getCorsOrigins: () => getConfig('VITE_CORS_ORIGINS'),
  getCorsMethods: () => getConfig('VITE_CORS_METHODS'),
  getCorsAllowedHeaders: () => getConfig('VITE_CORS_ALLOWED_HEADERS'),

  // Debug
  getDebug: () => getConfig('VITE_DEBUG') === 'true',
  getDebugVerbose: () => getConfig('VITE_DEBUG_VERBOSE') === 'true',

  // Locale
  getDefaultLocale: () => getConfig('VITE_DEFAULT_LOCALE'),

  // Polling
  getPollingIntervalSeconds: () => Number.parseInt(getConfig('VITE_POLLING_INTERVAL_SECONDS'), 10),

  // Version
  getAppVersion: () => '1.0.0', // Versão do package.json
};
